import { generateId, usedIds } from '../../core/ids'
import type {
  FeatureRecord,
  FeatureStatus,
  Placement,
  ProfileFinding,
  SketchConstraint,
  SketchEntity,
  SketchParams,
  SketchPlane,
  SketchSolution,
} from '../../generated/protocol'
import { featureSelectionItem } from '../../inspector/selection-values'
import { MESSAGES } from '../../messages'
import type { SolvedSketch } from '../../core/api'
import {
  CONSTRAINT_TITLES,
  constraintReferences,
  constraintTitle,
  constraintsFor,
  joinTitles,
  pickVerdict,
  type ConstraintType,
} from '../../sketch/constraints'
import { drawnPointItems, regionSelectionItem } from '../../sketch/hit'
import type { Vector2 } from '../../sketch/geometry'
import { framedPoints, outlineProblems } from '../../sketch/issues'
import { placementYAxis, worldBounds } from '../../sketch/placement'
import { isReservedReference } from '../../sketch/refs'
import type { IdSource } from '../../sketch/recipes'
import type {
  RefusedSketchCommit,
  SketchChange,
  SketchCommitResult,
  SketchToolId,
} from '../../sketch/session'
import { constraintTool } from '../../sketch/tools'
import {
  editingSession,
  selectDocumentUnits,
  selectFeatureLabel,
  selectReattachingSketch,
  selectSketchFaceLost,
  selectSketchRecord,
  selectSketchToEdit,
} from '../../state/selectors'
import type {
  AppState,
  AppStore,
  Notification,
  SelectionItem,
  SketchNotice,
  SketchSession,
} from '../../state/store'
import { entityUse } from '../../timeline/uses'
import { cameraAfterSketch, type Camera, type Vector3 } from '../../viewport/camera'
import type { ViewportHandle } from '../../viewport/controller'
import type { App } from '../createApp'
import { cancelCommandImplicitly } from './command'
import { coreRedo, coreUndo } from './document'
import { documentOf, showCoreError } from './edits'
import { reportChange } from './loss-notifications'
import { dismiss, notify } from './notifications'
import { inputLanded, inputOnItsWay, select, toggledByKey } from './selection'
import { commitSketchChange } from './sketch-commit'
import { createSketchOn, pickPlane, sketchPlaneOf } from './sketch-plane'
import { startInTurn } from './starts'
import { refreshUses } from './timeline'

export { commitSketchChange, createSketchOn, pickPlane, sketchPlaneOf }

// Sketch mode (ui.md 3.16): entering, leaving and navigating it (3.16.1, 3.16.11, 3.20), plane
// picking for a new sketch or for re-attaching one whose face is lost (3.16.1; `sketch-plane.ts`
// places the sketch on the picked plane), and the changes its tools commit: geometry, constraints,
// dimensions, construction and deletion. Every way into sketch mode is a start of 3.7 step 1
// (`startInTurn`), so it runs one at a time with feature commands and edits.

/** One step of the sketch view history (3.20): a camera to go back to, or a committed sketch edit. */
type HistoryEntry =
  | { readonly kind: 'view'; readonly camera: Camera }
  | { readonly kind: 'document' }

/** What sketch mode keeps for its session beside the store's `SketchSession` (3.16.11, 3.20). */
type SketchModeRecord = {
  readonly undo: HistoryEntry[]
  readonly redo: HistoryEntry[]
  /** Feature statuses when the session started; losses and failures since then are reported on finishing (4.2). */
  readonly statusesBefore: Readonly<Record<string, FeatureStatus>>
  /** Whether the edited sketch is one the session added, on the plane Sketch was given (3.16.1). */
  readonly added: boolean
}

const sessionRecords = new WeakMap<AppStore, SketchModeRecord>()

/** Each store's Sketch starts asked for and not run yet (3.16.1). */
const sketchStarts = new WeakMap<AppStore, number>()

/** The `fitBounds` margin of a framed sketch or profile issue: content fills 80 percent of the view. */
const SKETCH_FRAME_MARGIN = 0.1

/**
 * A fresh session on a sketch, or plane picking for a new one (no camera to go back to yet), with
 * nothing re-attached; plane picking for re-attaching a sketch sets `reattaching` (3.16.1).
 */
function sketchSession(sketchId: string | null, cameraBefore: Camera | null): SketchSession {
  return {
    sketchId,
    reattaching: null,
    tool: null,
    construction: false,
    showConstraints: true,
    selection: [],
    dragging: false,
    cameraBefore,
    notice: null,
  }
}

function editedSketch(app: App): string | null {
  const state = app.store.getState()
  return state.context === 'sketch' ? (state.sketch?.sketchId ?? null) : null
}

/**
 * Records the camera from before a view change in sketch mode (an orbit or pan gesture, a wheel
 * burst, a view cube click, fit, home or look at), so Mod+Z can go back to it.
 */
export function recordSketchView(app: App, before: Camera): void {
  const history = sessionRecords.get(app.store)
  if (history === undefined || editedSketch(app) === null) {
    return
  }
  history.undo.push({ kind: 'view', camera: before })
  history.redo.length = 0
}

/**
 * Records a committed sketch edit: Mod+Z in sketch mode undoes it in the core, and the used marks
 * ask for what later features use at the new revision (3.16.10). With `sketchId`, only while that
 * sketch is the one being edited.
 */
export function recordSketchEdit(app: App, sketchId: string | null = null): void {
  const edited = editedSketch(app)
  if (edited === null || (sketchId !== null && sketchId !== edited)) {
    return
  }
  void refreshUses(app)
  const history = sessionRecords.get(app.store)
  if (history !== undefined) {
    history.undo.push({ kind: 'document' })
    history.redo.length = 0
  }
}

/** Discards the sketch view history and the session's starting statuses, as leaving sketch mode does. */
export function leaveSketchHistory(app: App): void {
  sessionRecords.delete(app.store)
}

/** Steps back through the sketch view history; false when there is no step to take here. */
export async function undoInSketch(app: App): Promise<boolean> {
  return stepSketchHistory(app, 'undo')
}

/** Steps forward through the sketch view history; false when there is no step to take here. */
export async function redoInSketch(app: App): Promise<boolean> {
  return stepSketchHistory(app, 'redo')
}

async function stepSketchHistory(app: App, direction: 'undo' | 'redo'): Promise<boolean> {
  const history = sessionRecords.get(app.store)
  if (history === undefined || editedSketch(app) === null) {
    return false
  }
  const [from, to] =
    direction === 'undo' ? [history.undo, history.redo] : [history.redo, history.undo]
  const entry = from.pop()
  if (entry === undefined) {
    return false
  }
  if (entry.kind === 'document') {
    await (direction === 'undo' ? coreUndo(app) : coreRedo(app))
    to.push(entry)
    return true
  }
  const viewport = app.viewport()
  if (viewport !== null) {
    to.push({ kind: 'view', camera: viewport.getCamera() })
    viewport.setCamera(entry.camera, { animate: true, record: false })
  }
  return true
}

/** Ends a command or the edit of a sketch before plane picking starts (3.16.1). */
async function leaveForPlanePicking(app: App): Promise<void> {
  if (app.store.getState().command !== null) {
    await cancelCommandImplicitly(app)
  }
  if (editedSketch(app) !== null) {
    await finishSketch(app)
  }
}

/**
 * Whether the edited sketch is one the session added, with no edit committed in it, which a Sketch
 * that waited for the Sketch that added it keeps (3.16.1).
 */
function editingAddedSketch(app: App): boolean {
  const record = sessionRecords.get(app.store)
  return (
    editedSketch(app) !== null &&
    record?.added === true &&
    ![...record.undo, ...record.redo].some((entry) => entry.kind === 'document')
  )
}

/**
 * Starts Sketch, or the edit of the sketch `chosen` names, in its turn among the starts (3.7 step 1,
 * 3.16.1). A Sketch asked for while an earlier one is in progress keeps the sketch that one added.
 */
function sketchStart(app: App, chosen: Promise<string | null>): Promise<void> {
  const asked = sketchStarts.get(app.store) ?? 0
  const followsSketch = asked > 0
  sketchStarts.set(app.store, asked + 1)
  return startInTurn(app, async () => {
    try {
      const sketchId = await chosen
      if (sketchId !== null) {
        await openSketch(app, sketchId)
      } else if (!followsSketch || !editingAddedSketch(app)) {
        await openNewSketch(app)
      }
    } finally {
      sketchStarts.set(app.store, (sketchStarts.get(app.store) ?? 1) - 1)
    }
  })
}

/** Sketch mode for a new sketch: on the one selected plane, else plane picking (3.16.1). */
export function startNewSketch(app: App): Promise<void> {
  return sketchStart(app, Promise.resolve(null))
}

/**
 * The Sketch context outside sketch mode (3.3, 3.16.1): edits the selected sketch, or starts a new
 * one as Sketch does. It chooses from what the clicks and selection changes made before it leave,
 * as its tooltip named it, and takes its turn among the starts when it is asked for.
 */
export function editOrStartSketch(app: App): Promise<void> {
  const chosen = inputOnItsWay(app)
    ? inputLanded(app).then(() => selectSketchToEdit(app.store.getState()))
    : Promise.resolve(selectSketchToEdit(app.store.getState()))
  return sketchStart(app, chosen)
}

async function openNewSketch(app: App): Promise<void> {
  await leaveForPlanePicking(app)
  const { selection } = app.store.getState()
  const [only] = selection
  if (selection.length === 1 && only !== undefined && sketchPlaneOf(only) !== null) {
    await createSketchOn(app, only)
    return
  }
  openPlanePicking(app, null)
}

/** Plane picking (3.16.1) for a new sketch, or for the sketch `reattaching` is the id of. */
function openPlanePicking(app: App, reattaching: string | null): void {
  leaveSketchHistory(app)
  app.store.setState({ context: 'sketch', sketch: { ...sketchSession(null, null), reattaching } })
}

/**
 * Picks a new plane or face for a sketch whose face is lost (3.16.1, re-attaching): the plane
 * picking of a new sketch for that sketch, while repair mode shows its repair (3.7).
 */
export function reattachSketch(app: App, sketchId: string): Promise<void> {
  return startInTurn(app, async () => {
    if (selectSketchRecord(app.store.getState(), sketchId) === null) {
      return
    }
    await leaveForPlanePicking(app)
    openPlanePicking(app, sketchId)
  })
}

/**
 * Ends re-attaching once a fix attached the sketch (3.16.1): with its face no longer lost, the
 * sketch is edited, as after a pick.
 */
export function enterAttachedSketch(app: App): Promise<void> {
  return startInTurn(app, async () => {
    const state = app.store.getState()
    const sketchId = selectReattachingSketch(state)
    if (sketchId !== null && !selectSketchFaceLost(state, sketchId)) {
      await openSketch(app, sketchId)
    }
  })
}

/**
 * The 2D extent of a solution's entities in sketch coordinates, with the sketch origin when
 * `withOrigin`, or null without entities.
 */
function sketchExtent(
  solution: SketchSolution,
  withOrigin = false,
): { min: [number, number]; max: [number, number] } | null {
  const xs: number[] = []
  const ys: number[] = []
  function include(x: number, y: number): void {
    xs.push(x)
    ys.push(y)
  }
  if (withOrigin && solution.entities.length > 0) {
    include(0, 0)
  }
  for (const entity of solution.entities) {
    switch (entity.type) {
      case 'point':
        include(...entity.position)
        break
      case 'line':
        include(...entity.start)
        include(...entity.end)
        break
      case 'circle':
      case 'arc': {
        const [x, y] = entity.center
        include(x - entity.radius, y - entity.radius)
        include(x + entity.radius, y + entity.radius)
      }
    }
  }
  if (xs.length === 0) {
    return null
  }
  return { min: [Math.min(...xs), Math.min(...ys)], max: [Math.max(...xs), Math.max(...ys)] }
}

/** Zooms the view to a box in sketch coordinates, from `min` to `max`, with the sketch frame margin. */
function zoomToSketchBox(
  viewport: ViewportHandle,
  placement: Placement,
  min: Vector2,
  max: Vector2,
): void {
  const corners: Vector2[] = [min, [max[0], min[1]], [min[0], max[1]], max]
  const bounds = worldBounds(placement, corners)
  if (bounds !== null) {
    viewport.zoomToBounds(bounds, SKETCH_FRAME_MARGIN)
  }
}

/** Whether a sketch's constraints refer to its origin or an axis (sketch.md's reserved names). */
function refersToOrigin(constraints: readonly SketchConstraint[]): boolean {
  return constraints.some((constraint) =>
    constraintReferences(constraint).some((reference) => isReservedReference(reference)),
  )
}

/**
 * Looks at the sketch plane from its front and frames the sketch's entities, with its origin when
 * a constraint refers to the origin or an axis, so that axis and the dimension to it show (3.16.1).
 */
function lookAtSketch(app: App, sketchId: string, solution: SketchSolution): void {
  const viewport = app.viewport()
  if (viewport === null) {
    return
  }
  const { placement } = solution
  viewport.lookAlong(placement.normal, placementYAxis(placement), placement.origin)
  const constraints = storedSketchParams(app.store.getState(), sketchId)?.constraints ?? []
  const extent = sketchExtent(solution, refersToOrigin(constraints))
  if (extent !== null) {
    zoomToSketchBox(viewport, placement, extent.min, extent.max)
  }
}

/**
 * Resolves once every change queued so far, such as a sketch commit still on its way, has landed;
 * null when nothing is queued, so an idle queue adds no wait.
 */
function queuedChangesLanded(app: App): Promise<void> | null {
  return app.mutations.pending > 0 ? app.mutations.run(() => Promise.resolve()) : null
}

/**
 * Edits a sketch (3.16.1), in its turn among the starts (3.7 step 1): a browser row, a double-click
 * on a sketch item, "Back to sketch" and "Show '{sketch}'" open it once the starts asked for before
 * have run and the clicks made before it have landed.
 */
export function enterSketch(app: App, sketchId: string): Promise<void> {
  return startInTurn(app, () => openSketch(app, sketchId))
}

/** How `openSketch` enters a sketch. */
export type OpenSketchOptions = {
  /** The sketch was added by the start that enters it (`createSketchOn`). */
  readonly added?: boolean
}

/**
 * `enterSketch` in the turn of a start: Sketch context, the sketch shown, and the camera on its
 * plane (3.16.1). The sketch already being edited keeps its session; a store assembled in sketch
 * mode without one gets it.
 */
export async function openSketch(
  app: App,
  sketchId: string,
  { added = false }: OpenSketchOptions = {},
): Promise<void> {
  if (editedSketch(app) === sketchId && sessionRecords.has(app.store)) {
    return
  }
  if (app.store.getState().command !== null) {
    await cancelCommandImplicitly(app)
  }
  const landing = editedSketch(app) === null ? null : queuedChangesLanded(app)
  if (landing !== null) {
    // Moving on to another sketch: the commits of this one land first.
    await landing
  }
  const before = app.viewport()?.getCamera() ?? null
  // A session that moves on to another sketch keeps its starting statuses until it finishes, and
  // the camera from before it started, which looked at the model rather than at a sketch.
  const statusesBefore =
    sessionRecords.get(app.store)?.statusesBefore ?? documentOf(app)?.featureStatus ?? {}
  const cameraBefore =
    editedSketch(app) === null ? before : (app.store.getState().sketch?.cameraBefore ?? before)
  app.store.setState((state) => ({
    context: 'sketch',
    sketch: sketchSession(sketchId, cameraBefore),
    // The plane or face the sketch started from has done its job; a feature command run from the
    // sketch then preselects what the sketch made (3.3).
    selection: [],
    view: {
      ...state.view,
      sessionVisible: { ...state.view.sessionVisible, [`feature:${sketchId}`]: true },
    },
  }))
  const document = documentOf(app)
  if (document === null) {
    return
  }
  // The used marks show what later features use of the sketch (3.16.10).
  void refreshUses(app)
  let solution = document.sketches[sketchId]?.solution ?? null
  if (solution === null) {
    try {
      const solved = await app.core.solveSketch(document.documentHandle, sketchId)
      if (solved !== null) {
        solution = solved.solution
        showSketchSolution(app, sketchId, solved)
      }
    } catch (error) {
      // A refused solve leaves nothing to look at; the session still starts.
      showCoreError(app, error)
    }
  }
  if (solution !== null) {
    lookAtSketch(app, sketchId, solution)
  }
  // Set after the camera moved onto the sketch, so the history starts with the camera from before.
  sessionRecords.set(app.store, {
    undo: before === null ? [] : [{ kind: 'view', camera: before }],
    redo: [],
    statusesBefore,
    added,
  })
}

export type FinishSketchOptions = {
  /**
   * Selects what the finished sketch makes (3.16.11), as Finish sketch and the Model radio do. A
   * feature command preselects by its own rule (3.3), and the Print radio keeps the selection.
   */
  readonly selectResult?: boolean
}

/**
 * What finishing a sketch selects (3.16.11): its region when it has exactly one; without regions,
 * its non-construction point entities; otherwise, when it has entities, the sketch feature.
 */
function finishedSketchSelection(
  app: App,
  sketchId: string,
  solution: SketchSolution,
): SelectionItem[] {
  if (solution.regions.length === 1) {
    const [region] = solution.regions
    return region === undefined ? [] : [regionSelectionItem(sketchId, region, 'Region 1')]
  }
  if (solution.regions.length === 0) {
    const points = drawnPointItems(sketchId, solution)
    if (points.length > 0) {
      return points
    }
  }
  if (solution.entities.length === 0 || documentOf(app) === null) {
    return []
  }
  return [featureSelectionItem(sketchId, selectFeatureLabel(app.store.getState(), sketchId))]
}

/**
 * Turns a camera that still looks straight at the sketch plane back to the view from before the
 * sketch, or to iso, so the next feature's preview is seen in depth (3.16.11).
 */
function leaveSketchPlane(app: App, cameraBefore: Camera | null, normal: Vector3): void {
  const viewport = app.viewport()
  const next =
    viewport === null ? null : cameraAfterSketch(viewport.getCamera(), cameraBefore, normal)
  if (viewport !== null && next !== null) {
    viewport.setCamera(next, { animate: true })
  }
}

/**
 * Ends sketch mode (3.16.11): Model context, the sketch kept visible for the session, and a camera
 * that still looks straight at the sketch turned so the model is seen in depth. Features the
 * session's edits made fail are reported now rather than after each edit, and an outline with
 * problems is named in a warning that leads back to the sketch.
 */
export async function finishSketch(app: App, options: FinishSketchOptions = {}): Promise<void> {
  const landing = queuedChangesLanded(app)
  if (landing !== null) {
    // Commits still on their way land in the sketch first, so what finishing selects and reports
    // comes from the sketch as the user drew it.
    await landing
  }
  const state = app.store.getState()
  const sketchId = state.sketch?.sketchId ?? null
  const cameraBefore = state.sketch?.cameraBefore ?? null
  const statusesBefore = sessionRecords.get(app.store)?.statusesBefore ?? null
  leaveSketchHistory(app)
  app.store.setState((current) => ({
    context: 'model',
    sketch: null,
    view:
      sketchId === null
        ? current.view
        : {
            ...current.view,
            sessionVisible: { ...current.view.sessionVisible, [`feature:${sketchId}`]: true },
          },
  }))
  const document = documentOf(app)
  if (sketchId === null || document === null) {
    return
  }
  if (statusesBefore !== null) {
    reportChange(app, statusesBefore)
  }
  const solution = document.sketches[sketchId]?.solution ?? null
  if (solution !== null) {
    leaveSketchPlane(app, cameraBefore, solution.placement.normal)
  }
  if (options.selectResult === true && solution !== null) {
    select(app, finishedSketchSelection(app, sketchId, solution), 'replace')
  }
  const outline = solution === null ? null : outlineProblems(solution)
  if (outline !== null) {
    // A warning: the sketch stays valid and its other shapes still extrude (3.16.2).
    notify(app, {
      severity: 'warning',
      code: 'profile_problems',
      message: MESSAGES.profile_problems(
        selectFeatureLabel(app.store.getState(), sketchId),
        outline,
      ),
      actions: [
        {
          label: 'Back to sketch',
          run: () => {
            void enterSketch(app, sketchId)
          },
        },
      ],
    })
  }
}

function updateSession(app: App, change: Partial<SketchSession>): void {
  app.store.setState((state) =>
    state.sketch === null || state.sketch.sketchId === null
      ? {}
      : { sketch: { ...state.sketch, ...change } },
  )
}

/**
 * Activates a sketch tool, or none, cancelling the current operation. A constraint tool whose
 * operands are already selected applies its constraint at once instead of waiting for picks
 * (3.16.6).
 */
export function setSketchTool(app: App, tool: SketchToolId | null): void {
  const session = editingSession(app.store.getState())
  if (session === null) {
    return
  }
  const type = constraintTool(tool)
  const solution = solutionOf(app, session.sketchId)
  if (type !== null && solution !== null && session.selection.length > 0) {
    const constraints = constraintsFor(type, session.selection, solution, createSketchIds(app))
    if (constraints.length > 0) {
      updateSession(app, { tool: null })
      void applySketchConstraints(app, type, constraints)
      return
    }
    // Pick mode starts from the selection when more picks can complete it, else from nothing.
    const verdict = pickVerdict(type, session.selection, solution)
    updateSession(app, { tool, selection: verdict === 'partial' ? session.selection : [] })
    return
  }
  updateSession(app, { tool })
}

/** Replaces or toggles the in-sketch selection (3.16.10). */
export function selectInSketch(
  app: App,
  items: readonly SelectionItem[],
  mode: 'replace' | 'toggle',
): void {
  const session = editingSession(app.store.getState())
  if (session === null) {
    return
  }
  if (mode === 'replace') {
    updateSession(app, { selection: [...items] })
    return
  }
  updateSession(app, { selection: toggledByKey(session.selection, items) })
}

/** Marks whether a point drag is in progress, which the session shows as `dragging`. */
export function setSketchDragging(app: App, dragging: boolean): void {
  updateSession(app, { dragging })
}

// Ids and params.

/** Ids handed out per store and not yet in the document, so two pending changes never share one. */
const issuedIds = new WeakMap<AppStore, Set<string>>()

/**
 * Fresh entity and constraint ids (model.md 3.2): free in the whole document and never handed out
 * before in this session.
 */
export function createSketchIds(app: App): IdSource {
  const recipe = documentOf(app)?.recipe
  const taken = recipe === undefined ? new Set<string>() : usedIds(recipe)
  let issued = issuedIds.get(app.store)
  if (issued === undefined) {
    issued = new Set()
    issuedIds.set(app.store, issued)
  }
  const handedOut = issued
  return {
    next(prefix) {
      const id = generateId(prefix, (candidate) => taken.has(candidate) || handedOut.has(candidate))
      handedOut.add(id)
      return id
    },
  }
}

/** The params of the sketch with this id as the recipe stores them, or null. */
export function storedSketchParams(state: AppState, sketchId: string | null): SketchParams | null {
  const record = selectSketchRecord(state, sketchId)
  return record === null ? null : sketchParamsOf(record)
}

/** The body whose face the sketch is drawn on, or null for a sketch on a plane. */
export function sketchFaceBody(state: AppState, sketchId: string | null): string | null {
  const plane = storedSketchParams(state, sketchId)?.plane
  return plane?.kind === 'face' ? plane.body : null
}

/** A sketch record's params, read from the core's recipe (sketch.md 2.2). */
export function sketchParamsOf(record: FeatureRecord): SketchParams | null {
  const plane: unknown = record['plane']
  const entities: unknown = record['entities'] ?? []
  const constraints: unknown = record['constraints'] ?? []
  if (
    typeof plane !== 'object' ||
    plane === null ||
    !Array.isArray(entities) ||
    !Array.isArray(constraints)
  ) {
    return null
  }
  // The core validated the record against sketch.md before sending it.
  return {
    plane: plane as SketchPlane,
    entities: entities as SketchEntity[],
    constraints: constraints as SketchConstraint[],
  }
}

/** The current solution of a sketch, or null before it is solved. */
export function solutionOf(app: App, sketchId: string): SketchSolution | null {
  return documentOf(app)?.sketches[sketchId]?.solution ?? null
}

/** Shows a solution for a sketch, such as a drag frame or the stored geometry again. */
export function showSketchSolution(app: App, sketchId: string, solved: SolvedSketch): void {
  app.store.setState((state) =>
    state.document === null
      ? {}
      : {
          document: {
            ...state.document,
            sketches: { ...state.document.sketches, [sketchId]: solved },
          },
        },
  )
}

// Deleting and constraining (3.16.6, 3.16.10).

/** The ids a selection names as entities to delete: curves and point entities. */
function selectedEntities(items: readonly SelectionItem[]): string[] {
  return items.flatMap((item) => {
    const reference = item.sketchItem
    if (reference?.kind === 'entity') {
      return [reference.entity]
    }
    // A point entity is selected by its point reference, which is its bare id.
    if (
      reference?.kind === 'point' &&
      !reference.point.includes('.') &&
      reference.point !== 'origin'
    ) {
      return [reference.point]
    }
    return []
  })
}

/** How long the hint about a deleted entity that later features use stays (3.16.10). */
const ENTITY_USED_MILLISECONDS = 8_000

/**
 * The `entity_used` hint of deleting `entities` from the edited sketch (3.16.10): for the first of
 * them in stored order that a later feature uses, its first user and what that user uses of it;
 * null when nothing uses any of them.
 */
function entityUsedHint(app: App, entities: readonly string[]): string | null {
  const state = app.store.getState()
  const session = editingSession(state)
  const params = session === null ? null : storedSketchParams(state, session.sketchId)
  if (session === null || params === null) {
    return null
  }
  const deleted = new Set(entities)
  for (const entity of params.entities ?? []) {
    const use = deleted.has(entity.id) ? entityUse(state, session.sketchId, entity.id) : null
    if (use !== null) {
      const user = selectFeatureLabel(state, use.user)
      return MESSAGES.entity_used(user, use.noun, entity.type)
    }
  }
  return null
}

/** Shows `message` in place of the step prompt for 8 s, or until the next step prompt (3.16.10). */
function showSketchNotice(app: App, message: string): void {
  const notice: SketchNotice = { message }
  updateSession(app, { notice })
  setTimeout(() => {
    app.store.setState((state) =>
      state.sketch?.notice === notice ? { sketch: { ...state.sketch, notice: null } } : {},
    )
  }, ENTITY_USED_MILLISECONDS)
}

/**
 * Deletes the selected entities and constraints, plus every constraint that references a deleted
 * entity or one of its points, in one commit labeled "Delete" (3.16.10). Deleting an entity that a
 * later feature uses is not refused: the sketch hint says what uses it and how to keep it.
 */
export async function deleteSketchItems(app: App, items: readonly SelectionItem[]): Promise<void> {
  const entities = selectedEntities(items)
  const constraints = items.flatMap((item) =>
    item.sketchItem?.kind === 'constraint' ? [item.sketchItem.constraint] : [],
  )
  if (entities.length === 0 && constraints.length === 0) {
    return
  }
  const hint = entityUsedHint(app, entities)
  const result = await commitSketchChange(app, 'Delete', {
    removeEntities: entities,
    removeConstraints: constraints,
    requested: [],
    inferred: [],
  })
  if (result.outcome === 'committed') {
    const removed = new Set(items.map((item) => item.key))
    const session = editingSession(app.store.getState())
    if (session !== null) {
      updateSession(app, {
        selection: session.selection.filter((item) => !removed.has(item.key)),
      })
    }
    if (hint !== null) {
      showSketchNotice(app, hint)
    }
    return
  }
  showSketchRefusal(app, result)
}

/** Deletes constraints by id in one commit labeled "Delete", as the sketch problems list offers. */
export async function deleteSketchConstraints(
  app: App,
  constraints: readonly string[],
): Promise<void> {
  if (constraints.length === 0) {
    return
  }
  const result = await commitSketchChange(app, 'Delete', {
    removeConstraints: constraints,
    requested: [],
    inferred: [],
  })
  showSketchRefusal(app, result)
}

/** Shows why a change was not applied: the solver's or core's message as an error. */
function showSketchRefusal(app: App, result: SketchCommitResult): void {
  if (result.outcome === 'failed') {
    notify(app, { severity: 'error', message: result.message, actions: [] })
  }
}

/** The constraints of the edited sketch by id, for naming them in messages. */
function storedConstraints(app: App, sketchId: string): ReadonlyMap<string, SketchConstraint> {
  const params = storedSketchParams(app.store.getState(), sketchId)
  return new Map((params?.constraints ?? []).map((constraint) => [constraint.id, constraint]))
}

/**
 * Stored constraints named for a message by their titles (dimensions with their value, "Distance
 * 40"), joined with ", " and " and " (3.16.6).
 */
function conflictList(app: App, sketchId: string | null, ids: readonly string[]): string {
  const stored =
    sketchId === null ? new Map<string, SketchConstraint>() : storedConstraints(app, sketchId)
  const solution = sketchId === null ? null : solutionOf(app, sketchId)
  const units = selectDocumentUnits(app.store.getState())
  return joinTitles(
    ids.map((id) => {
      const constraint = stored.get(id)
      return constraint === undefined ? id : constraintTitle(constraint, solution, units)
    }),
  )
}

/**
 * Applies new constraints of one type (3.16.6): committed under the constraint's title, with the
 * over-constrained warning and its "Replace them" action, or the redundancy note, when the solver
 * refuses them. Resolves whether they were added.
 */
export async function applySketchConstraints(
  app: App,
  type: ConstraintType,
  constraints: readonly SketchConstraint[],
): Promise<boolean> {
  const sketchId = editingSession(app.store.getState())?.sketchId ?? null
  if (sketchId === null || constraints.length === 0) {
    return false
  }
  const title = CONSTRAINT_TITLES[type]
  const requested = constraints.map((constraint) => constraint.id)
  const change: SketchChange = { add: { entities: [], constraints }, requested, inferred: [] }
  const result = await commitSketchChange(app, title, change)
  switch (result.outcome) {
    case 'committed':
      updateSession(app, { selection: [] })
      return true
    case 'failed':
      showSketchRefusal(app, result)
      return false
    case 'over_constrained':
      if (result.redundant) {
        showOverConstrained(app, sketchId, title, result)
      } else {
        offerReplacement(app, sketchId, title, change, result)
      }
      return false
  }
}

type OverConstrained = Extract<SketchCommitResult, { outcome: 'over_constrained' }>

/**
 * The refusal of a change that over-constrains the sketch: `redundant_constraint` as info when the
 * new constraint changes nothing, else `over_constrained` as a warning naming the conflicting
 * constraints (3.16.6). Returns the notification's id.
 */
function showOverConstrained(
  app: App,
  sketchId: string | null,
  title: string,
  result: OverConstrained,
  actions: Notification['actions'] = [],
): string {
  return notify(app, {
    severity: result.redundant ? 'info' : 'warning',
    code: result.redundant ? 'redundant_constraint' : 'over_constrained',
    message: result.redundant
      ? MESSAGES.redundant_constraint(title)
      : MESSAGES.over_constrained(title, conflictList(app, sketchId, result.conflicting)),
    actions,
  })
}

/** The over-constrained warning with "Replace them", which swaps the conflicting constraints for the new ones. */
function offerReplacement(
  app: App,
  sketchId: string,
  title: string,
  change: SketchChange,
  result: OverConstrained,
): void {
  let id = ''
  id = showOverConstrained(app, sketchId, title, result, [
    {
      label: 'Replace them',
      run: () => {
        dismiss(app, id)
        void replaceConstraints(app, title, change, result.conflicting)
      },
    },
    {
      label: 'Cancel',
      run: () => {
        dismiss(app, id)
      },
    },
  ])
}

async function replaceConstraints(
  app: App,
  title: string,
  change: SketchChange,
  conflicting: readonly string[],
): Promise<void> {
  const result = await commitSketchChange(app, title, {
    ...change,
    removeConstraints: [...(change.removeConstraints ?? []), ...conflicting],
  })
  if (result.outcome === 'committed') {
    updateSession(app, { selection: [] })
    return
  }
  if (result.outcome === 'over_constrained') {
    const sketchId = editingSession(app.store.getState())?.sketchId ?? null
    notify(app, {
      severity: 'warning',
      message: MESSAGES.over_constrained(title, conflictList(app, sketchId, result.conflicting)),
      actions: [],
      code: 'over_constrained',
    })
    return
  }
  showSketchRefusal(app, result)
}

/** The least share of the sketch's size, on each axis, that framing sketch problems shows (3.16.8). */
const PROBLEM_FRAME_SHARE = 1 / 5

/** The range of the points along a sketch axis, grown about its center to at least `least`. */
function grownRange(
  points: readonly Vector2[],
  axis: 0 | 1,
  least: number,
): readonly [number, number] {
  const values = points.map((point) => point[axis])
  const low = Math.min(...values)
  const high = Math.max(...values)
  const center = (low + high) / 2
  const half = Math.max(high - low, least) / 2
  return [center - half, center + half]
}

/**
 * Frames points of a sketch (3.16.8 "Show"): their box in the sketch plane, grown about its center
 * to at least a fifth of the size of the sketch's entity bounds on each axis.
 */
function frameSketchPoints(app: App, solution: SketchSolution, points: readonly Vector2[]): void {
  const viewport = app.viewport()
  if (viewport === null || points.length === 0) {
    return
  }
  const extent = sketchExtent(solution)
  const least = (axis: 0 | 1): number =>
    extent === null ? 0 : PROBLEM_FRAME_SHARE * (extent.max[axis] - extent.min[axis])
  const [left, right] = grownRange(points, 0, least(0))
  const [bottom, top] = grownRange(points, 1, least(1))
  zoomToSketchBox(viewport, solution.placement, [left, bottom], [right, top])
}

/** Frames a profile issue of the edited sketch in the view (3.16.8 "Show"). */
export function showSketchFinding(app: App, finding: ProfileFinding): void {
  const sketchId = editingSession(app.store.getState())?.sketchId ?? null
  const solution = sketchId === null ? null : solutionOf(app, sketchId)
  if (solution !== null) {
    frameSketchPoints(app, solution, framedPoints(solution, finding))
  }
}

/**
 * "Show '{sketch}'" of the Extrude and Revolve reason (2.10): edits the sketch and frames all its
 * open ends, as the sketch problems popover's Show frames one finding (3.16.8).
 */
export async function showOpenEnds(app: App, sketchId: string): Promise<void> {
  await enterSketch(app, sketchId)
  const solution = solutionOf(app, sketchId)
  if (solution === null) {
    return
  }
  const points = solution.profile_findings
    .filter((finding) => finding.type === 'open_endpoint')
    .flatMap((finding) => framedPoints(solution, finding))
  frameSketchPoints(app, solution, points)
}

// Dimensions (3.16.7).

/** Whether a dimension drives the sketch; a reference dimension only reports (sketch.md). */
function isDriving(dimension: SketchConstraint): boolean {
  return !('driving' in dimension) || dimension.driving !== false
}

/** Adds a dimension the dimension tool made, labeled "Dimension"; a driving one is probed first. */
export function addSketchDimension(
  app: App,
  dimension: SketchConstraint,
): Promise<SketchCommitResult> {
  return commitSketchChange(app, 'Dimension', {
    add: { entities: [], constraints: [dimension] },
    requested: isDriving(dimension) ? [dimension.id] : [],
    inferred: [],
  })
}

/** Replaces a stored dimension with a new expression or driving flag, labeled "Change dimension". */
export function changeSketchDimension(
  app: App,
  dimension: SketchConstraint,
): Promise<SketchCommitResult> {
  return commitSketchChange(app, 'Change dimension', {
    replaceConstraint: dimension,
    requested: isDriving(dimension) ? [dimension.id] : [],
    inferred: [],
  })
}

/**
 * The sentence a refused dimension change shows in the dimension editor and the selection
 * inspector alike (3.16.7): the probe's message, the one the sketch would report once stored, or
 * `dimension_over_constrained` for an over-constrained probe without one.
 */
export function refusedDimensionMessage(result: RefusedSketchCommit): string {
  return result.message ?? MESSAGES.dimension_over_constrained()
}

/** Commits a drawing tool's shape; a refusal is shown, and resolves whether it was added. */
export async function addSketchShape(
  app: App,
  label: string,
  change: SketchChange,
): Promise<boolean> {
  const result = await commitSketchChange(app, label, change)
  switch (result.outcome) {
    case 'committed':
      return true
    case 'failed':
      showSketchRefusal(app, result)
      return false
    case 'over_constrained': {
      const sketchId = editingSession(app.store.getState())?.sketchId ?? null
      const added = change.add?.constraints.find(
        (constraint) => constraint.id === result.constraint,
      )
      const units = selectDocumentUnits(app.store.getState())
      const solution = sketchId === null ? null : solutionOf(app, sketchId)
      const title = added === undefined ? label : constraintTitle(added, solution, units)
      showOverConstrained(app, sketchId, title, result)
      return false
    }
  }
}
